import { SxProps, Theme } from '@mui/material';

import type { ButtonSize, ButtonVariant } from 'components/button/type';

import { colors } from 'theme/colors';

/**
 * Button Size Configurations
 * Defines padding, font size, line height, and min height for each size
 */
export const buttonSizes = {
  extraSmall: {
    padding: '6px 12px',
    fontSize: '12px',
    lineHeight: '16px',
    minHeight: '28px',
  },
  small: {
    padding: '8px 16px',
    fontSize: '14px',
    lineHeight: '20px',
    minHeight: '36px',
  },
  medium: {
    padding: '12px 24px',
    fontSize: '14px',
    lineHeight: '20px',
    minHeight: '44px',
  },
  large: {
    padding: '16px 32px',
    fontSize: '16px',
    lineHeight: '24px',
    minHeight: '52px',
  },
} as const;

/**
 * Size mapping for MUI Button component
 * Maps custom sizes to MUI's built-in sizes
 */
export const sizeMap: Record<ButtonSize, 'small' | 'medium' | 'large'> = {
  extraSmall: 'small',
  small: 'small',
  medium: 'medium',
  large: 'large',
};

/**
 * Base button styles
 * Common styles applied to all button variants
 */
export const baseButtonStyles: SxProps<Theme> = {
  textTransform: 'unset',
  borderRadius: 0,
  height: '39px',
  fontWeight: 400,
  transition: 'all 0.2s ease-in-out',
  boxShadow: 'none',
  '&:focus-visible': {
    outline: '2px solid',
    outlineOffset: '2px',
  },
};

/**
 * Primary Button Styles (Green Theme)
 */
const primaryStyles = {
  default: {
    backgroundColor: colors.button.dark, // #127A52
    color: colors.button.contrastText,
  },
  hover: {
    backgroundColor: colors.button.dark,
    opacity: 0.9,
    boxShadow: 'none',
  },
  active: {
    backgroundColor: colors.button.dark,
    opacity: 0.8,
  },
  focus: {
    outlineColor: colors.button.dark,
    outlineWidth: '3px',
  },
  disabled: {
    backgroundColor: colors.button.dark,
    color: colors.button.contrastText,
    opacity: 0.5,
  },
};

/**
 * Secondary Button Styles (Orange/Yellow Theme)
 */
const secondaryStyles = {
  default: {
    backgroundColor: '#FDBB39',
    color: '#131314',
  },
  hover: {
    backgroundColor: '#FFB74D',
    boxShadow: 'none',
  },
  active: {
    backgroundColor: '#F57C00',
  },
  focus: {
    outlineColor: '#FF9800',
    outlineWidth: '3px',
  },
  disabled: {
    backgroundColor: '#E5E7EB',
    color: '#9CA3AF',
  },
};

/**
 * Tertiary Button Styles (Gray Theme)
 */
const tertiaryStyles = {
  default: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
  },
  hover: {
    backgroundColor: '#E5E7EB',
    boxShadow: 'none',
  },
  active: {
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
  },
  focus: {
    outlineColor: '#374151',
    outlineWidth: '3px',
  },
  disabled: {
    backgroundColor: '#F9FAFB',
    color: '#D1D5DB',
  },
};

/**
 * Danger Button Styles (Red Theme)
 */
const dangerStyles = {
  default: {
    backgroundColor: colors.error.main,
    color: colors.error.contrastText,
  },
  hover: {
    backgroundColor: colors.error.light,
    boxShadow: 'none',
  },
  active: {
    backgroundColor: colors.error.dark,
  },
  focus: {
    outlineColor: colors.error.main,
    outlineWidth: '3px',
  },
  disabled: {
    backgroundColor: '#E5E7EB',
    color: '#9CA3AF',
  },
};

/**
 * Link Button Styles (Green Text, Transparent Background)
 */
const linkStyles = {
  default: {
    backgroundColor: 'transparent',
    color: colors.success.main,
    padding: 0,
    minHeight: 'auto',
  },
  hover: {
    backgroundColor: 'transparent',
    color: colors.success.light,
  },
  active: {
    backgroundColor: 'transparent',
    color: colors.success.dark,
  },
  focus: {
    outlineColor: colors.success.main,
    outlineWidth: '3px',
    backgroundColor: 'transparent',
  },
  disabled: {
    backgroundColor: 'transparent',
    color: '#9CA3AF',
  },
};

/**
 * Text Button Styles (Gray Text, Transparent Background)
 */
const textStyles = {
  default: {
    backgroundColor: 'transparent',
    color: '#374151',
    padding: 0,
    minHeight: 'auto',
  },
  hover: {
    backgroundColor: 'transparent',
    color: '#1F2937',
  },
  active: {
    backgroundColor: 'transparent',
    color: '#111827',
  },
  focus: {
    outlineColor: '#374151',
    outlineWidth: '3px',
    backgroundColor: 'transparent',
  },
  disabled: {
    backgroundColor: 'transparent',
    color: '#D1D5DB',
  },
};

/**
 * Variant styles mapping
 */
export const variantStyles = {
  primary: primaryStyles,
  secondary: secondaryStyles,
  tertiary: tertiaryStyles,
  danger: dangerStyles,
  link: linkStyles,
  text: textStyles,
} as const;

/**
 * Get button styles for a specific variant
 * @param variant - Button variant
 * @returns Style object for the variant
 */
export const getVariantStyles = (variant: ButtonVariant) => {
  return variantStyles[variant];
};

/**
 * Get size styles for a specific size
 * @param size - Button size
 * @returns Style object for the size
 */
export const getSizeStyles = (size: ButtonSize) => {
  return buttonSizes[size];
};

/**
 * Create custom sx styles for button component
 * @param variant - Button variant
 * @param size - Button size
 * @param height - Custom height (optional)
 * @param width - Custom width (optional)
 * @param sx - Additional sx styles (optional)
 * @returns Combined sx styles
 */
/**
 * Calculate height value from optional height parameter
 * @param height - Optional height value (string or number)
 * @param defaultHeight - Default height to use if height is undefined
 * @returns Formatted height string
 */
const calculateHeightValue = (
  height: string | number | undefined,
  defaultHeight: string
): string => {
  if (height === undefined) {
    return defaultHeight;
  }

  if (typeof height === 'number') {
    return `${height}px`;
  }

  return height;
};

export const createButtonSx = (
  variant: ButtonVariant,
  size: ButtonSize,
  height?: string | number,
  width?: string | number,
  sx?: SxProps<Theme>
): SxProps<Theme> => {
  const sizeStyles = getSizeStyles(size);
  const variantDefaultStyles = getVariantStyles(variant).default;

  const defaultHeight = '40px';
  const heightValue = calculateHeightValue(height, defaultHeight);

  const styles: SxProps<Theme> = {
    ...baseButtonStyles,
    ...sizeStyles,
    ...variantDefaultStyles,
    height: heightValue,
    minHeight: heightValue,
  };

  if (width !== undefined) {
    styles.width = typeof width === 'number' ? `${width}px` : width;
  }

  return {
    ...styles,
    ...sx,
  };
};
